/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  DateInput,
  Icon,
  Option,
  Pagination,
  SearchInput,
  Table,
  Typography,
  UtilityButton,
  UtilitySelect,
  useForm,
  useTheme,
} from '@quickbit/qb-design-system';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStorage } from 'react-use';
import {
  AlgorandProcessingRequestState,
  Filters,
  useSearch,
} from 'api/useAlgorandProcessingApi';
import { QBLoader } from 'components';

type FilterStore = {
  filters: Filters;
  pagination: {
    page: number;
    size: number;
  };
} | null;

const PAGE_SIZE = 16;
const DEFAULT_FROM = new Date();
DEFAULT_FROM.setDate(DEFAULT_FROM.getDate() - 3);

const buildDefaultFilterFormValues = (store: FilterStore) =>
  ({
    emailAddress: store?.filters.emailAddress || null,
    referenceId: store?.filters.referenceId || null,
    state: store?.filters.state || null,
    purchasedAtGt: store?.filters.purchasedAtGt
      ? new Date(store.filters.purchasedAtGt)
      : DEFAULT_FROM,
    purchasedAtLt: store?.filters.purchasedAtLt
      ? new Date(store.filters.purchasedAtLt)
      : null,
  } as Filters);

const STATE_FILTER_OPTIONS: {
  value: AlgorandProcessingRequestState;
  label: string;
}[] = Object.values(AlgorandProcessingRequestState).map((state) => ({
  value: state,
  label: state,
}));

export const AlgorandProcessingOverview = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();

  const [filterStore, setFilterStore] = useSessionStorage<FilterStore>(
    'qbAlgorandProcessingFilterStore'
  );

  const [page, setPage] = useState(filterStore?.pagination.page || 1);

  const { fields, setters } = useForm<Filters>(
    buildDefaultFilterFormValues(filterStore),
    {}
  );

  useEffect(() => {
    setFilterStore({
      filters: fields,
      pagination: { page, size: PAGE_SIZE },
    });
  }, [fields, page, setFilterStore]);

  const headers = [
    { label: 'Created At', cellKey: 'createdAt', sort: false },
    { label: 'Purchased At', cellKey: 'purchasedAt', sort: false },
    { label: 'Reference ID', cellKey: 'referenceId', sort: false },
    { label: 'Email Address', cellKey: 'emailAddress', sort: false },
    { label: 'Euro Amount', cellKey: 'euroAmount', sort: false },
    { label: 'State', cellKey: 'state', sort: false },
  ];

  const { data, isFetching, refetch } = useSearch(fields, {
    page,
    size: PAGE_SIZE,
  });

  const rows = useMemo(() => {
    if (!data) return [];

    // A workaround to reset pagination if the data rows are less than the page size
    setPage((state) => (data.aggregatedData.count <= PAGE_SIZE ? 1 : state));

    return data.algorandProcessingRequests.map((processingRequest) => ({
      id: processingRequest.referenceId,
      value: {
        ...processingRequest,
        purchasedAt: processingRequest.purchasedAt
          ? processingRequest.purchasedAt.toLocaleString()
          : '-',
        createdAt: processingRequest.createdAt.toLocaleString(),
      },
    }));
  }, [data]);

  const handleResetFilters = () => {
    setPage(1);
    setters.set(
      buildDefaultFilterFormValues({
        filters: {},
        pagination: { page: 1, size: PAGE_SIZE },
      })
    );
  };

  if (!data) return <QBLoader />;

  return (
    <>
      <Header>
        <Typography variant="subtitle">Algorand processing</Typography>
      </Header>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, auto)',
          gap: spacing.xxxs,
          alignItems: 'center',
        }}
      >
        <SearchInput
          placeholder="Reference ID"
          value={fields.referenceId ?? ''}
          onChange={(e) => setters.referenceId?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Email Address"
          value={fields.emailAddress ?? ''}
          onChange={(e) => setters.emailAddress?.set(e.target.value)}
        />
        <UtilitySelect
          label={fields.state || 'All States'}
          listTitle="State"
          options={STATE_FILTER_OPTIONS}
          value={
            fields.state
              ? STATE_FILTER_OPTIONS.find((stateOption) => {
                  return stateOption.value === fields.state;
                }) || null
              : null
          }
          onSelect={(data) => {
            setPage(1);
            const selectedOption = data as Option | null;
            setters.state?.set(
              selectedOption
                ? (selectedOption.value as AlgorandProcessingRequestState)
                : undefined
            );
          }}
        />
        <DateInput
          placeholder="All time"
          type="range"
          value={[
            fields.purchasedAtGt ? new Date(fields.purchasedAtGt) : null,
            fields.purchasedAtLt ? new Date(fields.purchasedAtLt) : null,
          ]}
          onChange={(range: [Date | null, Date | null]) => {
            setters.purchasedAtGt?.set(range[0] ?? undefined);
            setters.purchasedAtLt?.set(range[1] ?? undefined);
          }}
          withTime
        />
      </div>
      <div
        css={{
          marginTop: spacing.xxs,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xxxs,
        }}
      >
        <UtilityButton
          variant="text"
          text="Reset filters"
          onClick={handleResetFilters}
        />
        <UtilityButton
          variant="secondary"
          text="Refresh"
          onClick={() => refetch()}
          disabled={isFetching}
        />
        {isFetching && (
          <Icon
            name="spinner"
            className={{ svg: { color: palette.grey.default } }}
          />
        )}
      </div>
      <div css={{ marginTop: spacing.xxs }}>
        <Table
          headers={headers}
          rows={rows}
          onRowClick={(row) =>
            // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
            navigate(`/algorand-processing/${row.referenceId}`)
          }
        />
      </div>
      <div css={{ marginTop: spacing.xxs }}>
        <Pagination
          currentPage={page}
          onChange={setPage}
          numberOfElements={data.aggregatedData.count}
          pageSize={PAGE_SIZE}
        />
      </div>
    </>
  );
};
